/**
 * Impression A4 fiable (2026-09-14).
 *
 * Imprimer une page de l'application avec window.print() coupe tout ce qui dépasse d'un
 * conteneur fixe ou à hauteur limitée (fenêtre d'aperçu) : un relevé chargé de matières
 * perdait son bas (résumé, décision, signature). Ici le document est imprimé seul (iframe
 * ou fenêtre dédiée), et chaque feuille trop haute est réduite proportionnellement pour
 * tenir sur sa page A4, sans rien couper.
 */

// Arrondis du moteur d'impression : on garde quelques pixels de marge pour
// qu'un relevé « juste à la limite » ne bascule pas sur une 2e page (le dernier
// élément — bannière provisoire — débordait de quelques px). 12 px = ~1 ligne.
const MARGE_SECURITE_PX = 12;
// En dessous, le texte deviendrait illisible : la feuille s'étale alors sur deux pages
const ECHELLE_MIN = 0.6;

function echapper(texte: string): string {
  return texte.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

/** Document HTML A4 prêt à imprimer ; chaque feuille est un élément `.feuille-a4`. */
export function documentA4(opts: { titre: string; css?: string; corps: string }): string {
  return `<!DOCTYPE html><html lang="fr"><head><meta charset="UTF-8"/>
<base href="${window.location.origin}/"/>
<title>${echapper(opts.titre)}</title>
<style>
  @page { size: A4 portrait; margin: 0; }
  html, body { margin: 0; padding: 0; background: #fff; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  table { border-collapse: collapse; }
  .feuille-a4 { box-sizing: border-box; width: 210mm; position: relative; overflow: hidden; background: #fff;
    break-after: page; page-break-after: always; }
  .feuille-a4:last-child { break-after: auto; page-break-after: auto; }
  ${opts.css || ''}
</style></head><body>${opts.corps}</body></html>`;
}

function hauteurPageA4Px(doc: Document): number {
  const sonde = doc.createElement('div');
  sonde.style.cssText = 'position:absolute;top:0;left:0;width:1px;height:297mm;visibility:hidden;';
  doc.body.appendChild(sonde);
  const px = sonde.getBoundingClientRect().height;
  sonde.remove();
  return px;
}

/** Attend le chargement des images (logo, photo, QR) et des polices, au plus `delaiMaxMs`. */
export async function attendreRessources(doc: Document, delaiMaxMs = 5000): Promise<void> {
  const images = Array.from(doc.images).filter((img) => !img.complete);
  const chargement = Promise.all(images.map((img) => new Promise<void>((ok) => {
    img.addEventListener('load', () => ok(), { once: true });
    img.addEventListener('error', () => ok(), { once: true });
  })));
  const polices: Promise<unknown> = (doc as any).fonts?.ready ?? Promise.resolve();
  await Promise.race([
    Promise.all([chargement, polices]),
    new Promise((ok) => setTimeout(ok, delaiMaxMs)),
  ]);
}

/**
 * Réduit chaque feuille plus haute qu'une page A4 pour qu'elle tienne sur sa page.
 * La largeur est élargie d'autant avant le zoom : visuellement la feuille garde 210 mm.
 * Retourne l'échelle appliquée à chaque feuille (1 = inchangée).
 */
export function ajusterFeuillesA4(doc: Document, selecteur: string): number[] {
  const hauteurPage = hauteurPageA4Px(doc) - MARGE_SECURITE_PX;
  const zoomSupporte = !!doc.defaultView?.CSS?.supports?.('zoom', '0.5');
  const echelles: number[] = [];
  doc.querySelectorAll<HTMLElement>(selecteur).forEach((feuille) => {
    feuille.style.removeProperty('zoom');
    feuille.style.removeProperty('width');
    const { width, height } = feuille.getBoundingClientRect();
    if (!zoomSupporte || height <= hauteurPage) {
      echelles.push(1);
      return;
    }
    // Élargir la feuille ne peut que réduire sa hauteur (moins de retours à la ligne) :
    // une seule passe suffit pour garantir qu'elle tient sur la page.
    const echelle = Math.max(ECHELLE_MIN, Math.floor((hauteurPage / height) * 1000) / 1000);
    feuille.style.setProperty('width', `${width / echelle}px`);
    feuille.style.setProperty('zoom', String(echelle));
    echelles.push(echelle);
  });
  return echelles;
}

function prochaineImage(win: Window): Promise<void> {
  return new Promise((ok) => win.requestAnimationFrame(() => ok()));
}

/** Imprime un document A4 (voir documentA4) dans une iframe invisible, isolée de l'application. */
export async function imprimerHtmlDansIframe(html: string, selecteurFeuille = '.feuille-a4'): Promise<void> {
  const iframe = document.createElement('iframe');
  iframe.setAttribute('aria-hidden', 'true');
  iframe.style.cssText = 'position:fixed;left:0;top:0;width:210mm;height:297mm;border:0;opacity:0;pointer-events:none;z-index:-1;';
  document.body.appendChild(iframe);
  const win = iframe.contentWindow;
  const doc = iframe.contentDocument;
  if (!win || !doc) {
    iframe.remove();
    throw new Error("Impossible de préparer l'impression.");
  }
  doc.open();
  doc.write(html);
  doc.close();
  await attendreRessources(doc);
  ajusterFeuillesA4(doc, selecteurFeuille);
  await prochaineImage(win);

  let retiree = false;
  const retirer = () => {
    if (retiree) return;
    retiree = true;
    setTimeout(() => iframe.remove(), 300);
  };
  win.addEventListener('afterprint', retirer);
  win.focus();
  win.print();
  setTimeout(retirer, 120000);
}

/** Même ajustement pour un document déjà écrit dans une fenêtre dédiée (window.open). */
export async function imprimerFenetreA4(w: Window, selecteurFeuille = '.feuille-a4'): Promise<void> {
  await attendreRessources(w.document);
  ajusterFeuillesA4(w.document, selecteurFeuille);
  await prochaineImage(w);
  w.focus();
  w.print();
}
